// src/pages/TRIZProblemAnalyzer.jsx
// ブレイクスルー診断 v3 - 仕事モード / 趣味モード対応
import React, { useState, useEffect } from 'react';
import { analyzeProblem } from '../utils/trizAnalyzer';
import { TRIZ_PARAMETERS } from '../data/trizMatrix';
import { useAuth } from '../AuthContext';
import { useUserProfile } from '../contexts/nobishiro-dashboard/useUserProfile';
import {
  Zap, Target, Lightbulb, RotateCcw, ArrowRight,
  Sparkles, Bot, ChevronDown, ChevronUp,
  Briefcase, Gamepad2, History, Trash2, Clock
} from 'lucide-react';

/* ════════════════════════════════════════
   モード設定
════════════════════════════════════════ */
const MODES = {
  work: {
    key: 'work',
    label: '仕事モード',
    icon: Briefcase,
    color:       '#3b6fd4',
    colorLight:  '#eff6ff',
    colorBorder: '#bfdbfe',
    colorMid:    '#2855b8',
    colorShadow: 'rgba(59,111,212,0.3)',
    colorBadge:  '#1d4ed8',
    tagBg:       '#dbeafe',
    tagColor:    '#1e40af',
    theme: 'blue',
    placeholder: '例：営業成績がここ3ヶ月横ばいで、何をしてもなかなか伸びない。新しい手法を試しても長続きしない…',
    step1Title:  '今、どんな仕事の壁にぶつかっていますか？',
    step1Desc:   '業務・スキル・人間関係・キャリアなど、仕事に関する課題を具体的に書いてください。',
    step2Title:  'ビジネス上のトレードオフを特定してください',
    step2Desc:   '改善したい指標と、それによって悪化する指標を選んでください。',
    step3Title:  'あなたの仕事への突破口',
    step3Desc:   '40の人材発達法則から、ビジネス課題に効く突破口を選定しました。',
    resultLabel: '突破口',
    whyTitle:    '◆ なぜ仕事に効くのか',
    hintTitle:   '💼 実践のヒント',
    improveLabel:'↑ 改善したいビジネス指標',
    worsenLabel: '↓ 悪化してしまう指標',
    historyTag:  '仕事',
  },
  hobby: {
    key: 'hobby',
    label: '趣味モード',
    icon: Gamepad2,
    color:       '#ea8c2f',
    colorLight:  '#fff7ed',
    colorBorder: '#fed7aa',
    colorMid:    '#c2690a',
    colorShadow: 'rgba(234,140,47,0.3)',
    colorBadge:  '#c2410c',
    tagBg:       '#ffedd5',
    tagColor:    '#9a3412',
    theme: 'orange',
    placeholder: '例：ギターを1年練習しているのに、コードチェンジがうまくいかない。練習量は十分なはずなのに…',
    step1Title:  '趣味でどんな壁にぶつかっていますか？',
    step1Desc:   'スポーツ・音楽・ゲーム・創作など、趣味に関する悩みを自由に書いてください。',
    step2Title:  '何と何がトレードオフになっていますか？',
    step2Desc:   '上達を妨げているトレードオフを選んでください。ピンとこなければスキップしてもOK。',
    step3Title:  'あなたの趣味への突破口',
    step3Desc:   '40の発達法則から、あなたの趣味の壁を乗り越えるヒントを見つけました。',
    resultLabel: 'アドバイス',
    whyTitle:    '◆ なぜ趣味に効くのか',
    hintTitle:   '🎯 上達のコツ',
    improveLabel:'↑ もっと良くしたいこと',
    worsenLabel: '↓ そのせいで犠牲になること',
    historyTag:  '趣味',
  },
};

/* ─── 趣味モード用パラメータ表示名マッピング ─── */
const HOBBY_PARAM_NAMES = {
  '1':  '上達スピード',
  '2':  '動きの正確さ',
  '3':  'スタミナ・持続力',
  '4':  '安定感・信頼性',
  '5':  'スピード',
  '6':  '力強さ',
  '7':  '可動域・柔軟性',
  '8':  '集中力',
  '9':  '技術の複雑さ',
  '10': '構成・デザイン',
  '11': 'ストレス・疲労',
  '12': '形・フォーム',
  '13': '安定性・バランス',
  '14': '強度',
  '15': '耐久性',
  '16': '動きの時間',
  '17': '温度・コンディション',
  '18': '明るさ・視認性',
  '19': 'エネルギー消費',
  '20': '消耗・劣化',
  '21': '出力・パワー',
  '22': 'エネルギー損失',
  '23': '素材・道具の質',
  '24': '情報量・複雑さ',
  '25': '時間の無駄',
  '26': '量・数',
  '27': '信頼性',
  '28': '計測・把握',
  '29': '精度・正確さ',
  '30': '悪影響・副作用',
  '31': '有害な変化',
  '32': '製造・準備のしやすさ',
  '33': '使いやすさ',
  '34': '修理・回復のしやすさ',
  '35': '柔軟性・応用力',
  '36': '機能の複雑さ',
  '37': '制御のしやすさ',
  '38': '自動化・習慣化',
  '39': '生産性',
};

/* ─── パラメータリスト（モード別表示名） ─── */
const getParams = (mode) =>
  TRIZ_PARAMETERS.map(p => ({
    ...p,
    name: mode === 'hobby'
      ? (HOBBY_PARAM_NAMES[String(p.id)] || p.name)
      : p.name,
  }));

/* ════════════════════════════════════════
   UIパーツ
════════════════════════════════════════ */

/* ─── モードタブ ─── */
const ModeTabs = ({ mode, onChange }) => (
  <div style={{
    display: 'flex', background: '#f3f4f6', borderRadius: 12,
    padding: 4, marginBottom: 28, gap: 4,
  }}>
    {Object.values(MODES).map(m => {
      const Icon = m.icon;
      const active = mode === m.key;
      return (
        <button
          key={m.key}
          onClick={() => onChange(m.key)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            background: active ? '#fff' : 'transparent',
            color: active ? m.color : '#6b7280',
            border: 'none', borderRadius: 9,
            padding: '10px 16px',
            fontSize: 14, fontWeight: active ? 700 : 500,
            cursor: 'pointer',
            boxShadow: active ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.25s',
          }}
        >
          <Icon size={15} />
          {m.label}
        </button>
      );
    })}
  </div>
);

/* ─── ステッパー ─── */
const Stepper = ({ current, mode }) => {
  const m = MODES[mode];
  const STEPS = [
    { num: 1, label: '課題を話す' },
    { num: 2, label: '壁を特定する' },
    { num: 3, label: '突破口を受け取る' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28 }}>
      {STEPS.map((s, i) => {
        const done   = current > s.num;
        const active = current === s.num;
        return (
          <React.Fragment key={s.num}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, minWidth: 96 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%',
                background: done ? m.color : active ? m.colorLight : '#f3f4f6',
                border: `2px solid ${done ? m.color : active ? m.color : '#e5e7eb'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: done ? '#fff' : active ? m.color : '#9ca3af',
                fontSize: 12, fontWeight: 700, transition: 'all 0.3s',
              }}>
                {done ? '✓' : s.num}
              </div>
              <span style={{
                fontSize: 11,
                color: active ? m.color : done ? '#6b7280' : '#9ca3af',
                fontWeight: active ? 700 : 400, whiteSpace: 'nowrap',
              }}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, marginBottom: 20,
                background: current > s.num ? m.color : '#e5e7eb',
                transition: 'background 0.4s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ─── テキストエリア ─── */
const StyledTextArea = ({ value, onChange, placeholder, rows, accentColor }) => (
  <textarea
    value={value} onChange={onChange} placeholder={placeholder} rows={rows || 4}
    style={{
      width: '100%', boxSizing: 'border-box',
      border: `1.5px solid ${value ? accentColor : '#e5e7eb'}`,
      borderRadius: 10, padding: '13px 15px',
      color: '#1f2937', fontSize: 14, lineHeight: 1.7,
      resize: 'vertical', outline: 'none', fontFamily: 'inherit',
      background: '#fff', transition: 'border-color 0.2s, box-shadow 0.2s',
    }}
    onFocus={e => { e.target.style.borderColor = accentColor; e.target.style.boxShadow = `0 0 0 3px ${accentColor}22`; }}
    onBlur={e => { e.target.style.borderColor = value ? accentColor : '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
  />
);

/* ─── セレクト ─── */
const StyledSelect = ({ value, onChange, options, placeholder, accentColor }) => (
  <div style={{ position: 'relative' }}>
    <select
      value={value} onChange={onChange}
      style={{
        width: '100%',
        border: `1.5px solid ${value ? accentColor : '#e5e7eb'}`,
        borderRadius: 10, padding: '11px 40px 11px 14px',
        color: value ? '#1f2937' : '#9ca3af',
        fontSize: 13, outline: 'none', cursor: 'pointer',
        fontFamily: 'inherit', appearance: 'none', background: '#fff',
        transition: 'border-color 0.2s',
      }}
      onFocus={e => { e.target.style.borderColor = accentColor; e.target.style.boxShadow = `0 0 0 3px ${accentColor}22`; }}
      onBlur={e => { e.target.style.borderColor = value ? accentColor : '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
    >
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
    <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }}>▾</div>
  </div>
);

/* ─── プライマリボタン ─── */
const PrimaryButton = ({ onClick, disabled, loading, children, mode }) => {
  const m = MODES[mode];
  return (
    <button
      onClick={onClick} disabled={disabled || loading}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: disabled || loading ? '#e5e7eb' : `linear-gradient(135deg, ${m.color} 0%, ${m.colorMid} 100%)`,
        color: disabled || loading ? '#9ca3af' : '#fff',
        border: 'none', borderRadius: 10, padding: '12px 24px',
        fontSize: 14, fontWeight: 700,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        boxShadow: disabled || loading ? 'none' : `0 4px 14px ${m.colorShadow}`,
      }}
      onMouseEnter={e => { if (!disabled && !loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
    >
      {loading ? <><span style={{ animation: 'spin 0.8s linear infinite', display: 'inline-block' }}>⟳</span> 分析中...</> : children}
    </button>
  );
};

/* ─── 突破口カード ─── */
const BreakthroughCard = ({ result, rank, mode }) => {
  const [expanded, setExpanded] = useState(false);
  const m   = MODES[mode];
  const p   = result.principle || result;
  const reasons  = Array.isArray(result.reasons) ? result.reasons : [];
  const examples = Array.isArray(p.examples) ? p.examples : [];
  const stage    = p.stage || {};
  const score    = result.score || 0;

  const rankConfig = [
    { label: mode === 'hobby' ? '特におすすめ' : '最有力', icon: '🥇', bg: '#fef3c7', color: '#92400e', border: '#fbbf24' },
    { label: mode === 'hobby' ? 'おすすめ'     : '有力',   icon: '🥈', bg: '#ede9fe', color: '#5b21b6', border: '#8b5cf6' },
    { label: mode === 'hobby' ? '参考に'        : '候補',   icon: '🥉', bg: '#e0f2fe', color: '#075985', border: '#38bdf8' },
  ];
  const cfg = rankConfig[rank] || rankConfig[2];

  return (
    <div style={{
      background: '#fff',
      border: `1.5px solid ${rank === 0 ? '#fbbf24' : '#e5e7eb'}`,
      borderRadius: 14, overflow: 'hidden', marginBottom: 16,
      boxShadow: rank === 0 ? '0 4px 20px rgba(251,191,36,0.15)' : '0 1px 4px rgba(0,0,0,0.06)',
      animation: 'fadeIn 0.35s ease both',
      animationDelay: `${rank * 0.08}s`,
    }}>
      {/* ヘッダー */}
      <div style={{
        background: rank === 0 ? '#fffbeb' : '#f9fafb',
        padding: '16px 20px 13px',
        borderBottom: '1px solid #f3f4f6',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <span style={{
            background: cfg.bg, color: cfg.color,
            border: `1px solid ${cfg.border}`,
            fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
            padding: '3px 10px', borderRadius: 20,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>{cfg.icon} {cfg.label}</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>{m.resultLabel} #{rank + 1}</span>
          {score > 0 && <span style={{ fontSize: 11, color: '#9ca3af' }}>マッチ度 {Math.min(score * 5, 98)}%</span>}
        </div>
        <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>
          {p.number ? `Law ${p.number} ` : p.id ? `Law ${p.id} ` : ''}「{p.name || '法則'}」
        </h3>
        {p.kajosetsu_layer && (
          <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, color: '#6b7280', background: '#f3f4f6', borderRadius: 6, padding: '2px 10px' }}>
            {p.kajosetsu_layer}
          </span>
        )}
      </div>

      {/* ボディ */}
      <div style={{ padding: '16px 20px' }}>
        {/* なぜ効くのか */}
        {reasons.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: m.color, letterSpacing: '0.05em', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 5 }}>
              🎯 {m.whyTitle}
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.75, borderLeft: `3px solid ${m.color}`, paddingLeft: 12 }}>
              {reasons.join('。')}
            </p>
          </div>
        )}

        {/* 概要 */}
        {(p.description || p.definition) && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: m.color, letterSpacing: '0.05em', marginBottom: 7 }}>◆ 概要</div>
            <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.75, borderLeft: `3px solid ${m.color}`, paddingLeft: 12 }}>
              {p.description || p.definition}
            </p>
          </div>
        )}

        {/* ヒント */}
        {(p.best_time || stage.advanced) && (
          <div style={{
            background: m.colorLight, border: `1px solid ${m.colorBorder}`,
            borderRadius: 10, padding: '12px 15px', marginBottom: 14,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: m.colorBadge, letterSpacing: '0.05em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Sparkles size={12} /> {m.hintTitle}
            </div>
            {p.best_time && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 16 }}>⏰</span>
                <span style={{ fontSize: 12, color: m.colorBadge, fontWeight: 600 }}>{p.best_time}</span>
              </div>
            )}
            {stage.advanced && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>🏆</span>
                <p style={{ margin: 0, fontSize: 13, color: '#1e3a8a', lineHeight: 1.65 }}>上級者は「{stage.advanced}」の状態を目指しましょう。</p>
              </div>
            )}
          </div>
        )}

        {/* アコーディオン */}
        {(examples.length > 0 || stage.beginner) && (
          <button
            onClick={() => setExpanded(!expanded)}
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 12, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 5, transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = m.color}
            onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            {expanded ? '詳細を閉じる' : '実践例・発達段階を見る'}
          </button>
        )}

        {expanded && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6', animation: 'fadeIn 0.2s ease' }}>
            {examples.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>💡 実践例</div>
                {examples.map((ex, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 5, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                    <span style={{ color: m.color, flexShrink: 0 }}>→</span>
                    <span>{ex}</span>
                  </div>
                ))}
              </div>
            )}
            {(stage.beginner || stage.intermediate || stage.advanced) && (
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>🌱 発達段階</div>
                {[
                  {
                    key: 'beginner',
                    label: '初級',
                    bg: '#f0fdf4', color: '#166534', border: '#bbf7d0',
                    icon: (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <circle cx="14" cy="14" r="13" fill="#dcfce7" stroke="#86efac" strokeWidth="1"/>
                        <circle cx="14" cy="11" r="3.5" fill="#16a34a"/>
                        <path d="M8 22c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="10" cy="14" r="1.2" fill="#16a34a" opacity="0.4"/>
                        <circle cx="18" cy="14" r="1.2" fill="#16a34a" opacity="0.4"/>
                      </svg>
                    ),
                    desc: '学び始め',
                  },
                  {
                    key: 'intermediate',
                    label: '中級',
                    bg: '#fefce8', color: '#713f12', border: '#fde68a',
                    icon: (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <circle cx="14" cy="14" r="13" fill="#fef9c3" stroke="#fde047" strokeWidth="1"/>
                        <circle cx="10" cy="12" r="2.8" fill="#ca8a04"/>
                        <circle cx="18" cy="12" r="2.8" fill="#ca8a04"/>
                        <path d="M7 21c0-3 2.239-4.5 5-4.5" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M21 21c0-3-2.239-4.5-5-4.5" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M14 16.5v1.5" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M11 21h6" stroke="#ca8a04" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    ),
                    desc: '実践中',
                  },
                  {
                    key: 'advanced',
                    label: '上級',
                    bg: m.colorLight, color: m.colorBadge, border: m.colorBorder,
                    icon: (
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                        <circle cx="14" cy="14" r="13" fill="#ede9fe" stroke="#c4b5fd" strokeWidth="1"/>
                        <circle cx="14" cy="10" r="3" fill="#7c3aed"/>
                        <path d="M8 20c0-2.761 2.686-4.5 6-4.5s6 1.739 6 4.5" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M14 6l.5 1.5H16l-1.3.9.5 1.6L14 9l-1.2 1 .5-1.6L12 7.5h1.5L14 6z" fill="#7c3aed"/>
                      </svg>
                    ),
                    desc: '熟達',
                  },
                ].map(({ key, label, bg, color, border, icon, desc }) => stage[key] && (
                  <div key={key} style={{ display: 'flex', gap: 10, marginBottom: 10, padding: '10px 12px', background: bg, border: `1px solid ${border}`, borderRadius: 10, alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                      {icon}
                      <span style={{ fontSize: 9, fontWeight: 700, color, letterSpacing: '0.03em' }}>{label}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color, marginBottom: 3 }}>{desc}のレベル</div>
                      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.65 }}>{stage[key]}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── 履歴カード ─── */
const HistoryCard = ({ item, onDelete, onRestore, m }) => (
  <div
    onClick={() => onRestore(item)}
    style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
      padding: '13px 16px', marginBottom: 10,
      display: 'flex', alignItems: 'flex-start', gap: 12,
      transition: 'box-shadow 0.2s, border-color 0.2s',
      cursor: 'pointer',
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = m.color; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
  >
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
        <span style={{
          background: item.mode === 'work' ? '#dbeafe' : '#ffedd5',
          color: item.mode === 'work' ? '#1e40af' : '#9a3412',
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        }}>
          {MODES[item.mode]?.historyTag || item.mode}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 3 }}>
          <Clock size={11} />
          {new Date(item.timestamp).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: m.color, fontWeight: 600 }}>
          クリックで復元 →
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
        「{item.description.slice(0, 60)}{item.description.length > 60 ? '…' : ''}」
      </p>
      {item.results && item.results.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {item.results.slice(0, 3).map((r, i) => {
            const p = r.principle || r;
            return (
              <span key={i} style={{ fontSize: 11, color: m.color, background: m.colorLight, border: `1px solid ${m.colorBorder}`, borderRadius: 20, padding: '2px 8px' }}>
                {p.name || ''}
              </span>
            );
          })}
        </div>
      )}
    </div>
    <button
      onClick={e => { e.stopPropagation(); onDelete(item.id); }}
      style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', padding: 4, borderRadius: 6, transition: 'color 0.2s', flexShrink: 0 }}
      onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.color = '#ef4444'; }}
      onMouseLeave={e => { e.stopPropagation(); e.currentTarget.style.color = '#d1d5db'; }}
    >
      <Trash2 size={14} />
    </button>
  </div>
);

/* ════════════════════════════════════════
   メインコンポーネント
════════════════════════════════════════ */
const HISTORY_KEY = 'nobishiro_breakthrough_history';

export default function TRIZProblemAnalyzer() {
  const { user } = useAuth();
  const { profile } = useUserProfile(user?.uid);

  const [mode, setMode]                       = useState('work');
  const [showHistory, setShowHistory]         = useState(false);
  const [history, setHistory]                 = useState([]);
  const [currentStep, setCurrentStep]         = useState(1);
  const [problemData, setProblemData]         = useState({ description: '', improvingFactor: '', worseningFactor: '' });
  const [usePersonalization, setUsePersonalization] = useState(true);
  const [analyzedResults, setAnalyzedResults] = useState(null);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);

  const m      = MODES[mode];
  const params = getParams(mode);

  // 履歴をlocalStorageから読み込み
  useEffect(() => {
    try {
      const saved = localStorage.getItem(HISTORY_KEY);
      if (saved) setHistory(JSON.parse(saved));
    } catch (e) { /* ignore */ }
  }, []);

  // モード切替時にSTEP1に戻す
  const handleModeChange = (newMode) => {
    setMode(newMode);
    setCurrentStep(1);
    setProblemData({ description: '', improvingFactor: '', worseningFactor: '' });
    setAnalyzedResults(null);
    setError(null);
    setShowHistory(false);
  };

  // 分析実行
  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = await analyzeProblem(problemData, usePersonalization ? profile : null);

      let results;
      if (Array.isArray(raw)) {
        results = raw;
      } else if (raw && Array.isArray(raw.recommendations)) {
        results = raw.recommendations;
      } else if (raw && typeof raw === 'object') {
        results = Object.values(raw).filter(v => v && typeof v === 'object');
      } else {
        results = [];
      }

      setAnalyzedResults(results);
      setCurrentStep(3);

      // 履歴に保存
      const entry = {
        id: Date.now(),
        mode,
        description: problemData.description,
        improvingFactor: problemData.improvingFactor,
        worseningFactor: problemData.worseningFactor,
        results: results.slice(0, 3),
        timestamp: new Date().toISOString(),
      };
      const updated = [entry, ...history].slice(0, 20); // 最大20件
      setHistory(updated);
      try { localStorage.setItem(HISTORY_KEY, JSON.stringify(updated)); } catch (e) { /* ignore */ }

    } catch (err) {
      console.error('Analysis failed:', err);
      setError('分析中にエラーが発生しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleRestart = () => {
    setCurrentStep(1);
    setProblemData({ description: '', improvingFactor: '', worseningFactor: '' });
    setAnalyzedResults(null);
    setError(null);
  };

  const handleDeleteHistory = (id) => {
    const updated = history.filter(h => h.id !== id);
    setHistory(updated);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(updated)); } catch (e) { /* ignore */ }
  };

  // 履歴から結果を復元
  const handleRestoreHistory = (item) => {
    setMode(item.mode);
    setProblemData({
      description: item.description || '',
      improvingFactor: item.improvingFactor || '',
      worseningFactor: item.worseningFactor || '',
    });
    setAnalyzedResults(item.results || []);
    setCurrentStep(3);
    setShowHistory(false);
  };

  const filteredHistory = history.filter(h => h.mode === mode);

  return (
    <div style={{
      background: '#f8fafc', minHeight: 480, borderRadius: 16,
      padding: '28px 24px',
      fontFamily: "'Hiragino Sans', 'Yu Gothic', 'Noto Sans JP', sans-serif",
    }}>
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes fadeIn  { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: none; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(14px);  } to { opacity: 1; transform: none; } }
        * { -webkit-font-smoothing: antialiased; }
      `}</style>

      {/* ─── ヘッダー ─── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
            <div style={{ background: m.colorLight, border: `1px solid ${m.colorBorder}`, borderRadius: 8, padding: 7, display: 'flex', transition: 'all 0.3s' }}>
              <Zap size={16} color={m.color} />
            </div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>
              ブレイクスルー診断
            </h1>
            <span style={{ background: m.color, color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', padding: '3px 7px', borderRadius: 4 }}>
              NEW
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
            40の人材発達法則で、成長の壁を突破する突破口を見つける
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* 履歴ボタン */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: showHistory ? m.colorLight : '#fff',
              border: `1.5px solid ${showHistory ? m.color : '#e5e7eb'}`,
              borderRadius: 10, padding: '9px 14px',
              color: showHistory ? m.color : '#6b7280',
              fontSize: 12, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s',
              position: 'relative',
            }}
          >
            <History size={14} />
            履歴
            {filteredHistory.length > 0 && (
              <span style={{
                background: m.color, color: '#fff',
                fontSize: 9, fontWeight: 800,
                width: 16, height: 16, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'absolute', top: -5, right: -5,
              }}>
                {filteredHistory.length}
              </span>
            )}
          </button>

          {/* AI壁打ちパートナーモード */}
          <div
            onClick={() => setUsePersonalization(!usePersonalization)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: '#fff', border: `1.5px solid ${usePersonalization ? m.color : '#e5e7eb'}`,
              borderRadius: 10, padding: '9px 14px',
              cursor: 'pointer', userSelect: 'none', transition: 'all 0.2s',
            }}
          >
            <Bot size={14} color={usePersonalization ? m.color : '#9ca3af'} />
            <span style={{ fontSize: 12, fontWeight: 600, color: usePersonalization ? m.color : '#9ca3af' }}>
              AI壁打ちパートナーモード
            </span>
            <div style={{ width: 32, height: 17, borderRadius: 9, background: usePersonalization ? m.color : '#d1d5db', position: 'relative', flexShrink: 0, transition: 'background 0.25s' }}>
              <div style={{ width: 11, height: 11, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: usePersonalization ? 18 : 3, transition: 'left 0.25s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        </div>
      </div>

      {/* ─── モードタブ ─── */}
      <ModeTabs mode={mode} onChange={handleModeChange} />

      {/* ─── 履歴パネル ─── */}
      {showHistory && (
        <div style={{ animation: 'fadeIn 0.25s ease', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 7 }}>
              <History size={16} color={m.color} />
              {m.label}の診断履歴
            </h3>
            <span style={{ fontSize: 12, color: '#9ca3af' }}>{filteredHistory.length}件</span>
          </div>
          {filteredHistory.length === 0 ? (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              まだ履歴がありません
            </div>
          ) : (
            filteredHistory.map(item => (
              <HistoryCard key={item.id} item={item} onDelete={handleDeleteHistory} onRestore={handleRestoreHistory} m={m} />
            ))
          )}
          <div style={{ height: 1, background: '#e5e7eb', margin: '20px 0' }} />
        </div>
      )}

      {/* ─── ステッパー ─── */}
      {!showHistory && <Stepper current={currentStep} mode={mode} />}

      {/* ─── STEP 1: 課題を話す ─── */}
      {!showHistory && currentStep === 1 && (
        <div style={{ animation: 'slideUp 0.3s ease' }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={18} color={m.color} />
              {m.step1Title}
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280', paddingLeft: 26, lineHeight: 1.6 }}>
              {m.step1Desc}
            </p>
          </div>

          <StyledTextArea
            value={problemData.description}
            onChange={e => setProblemData({ ...problemData, description: e.target.value })}
            placeholder={m.placeholder}
            rows={5}
            accentColor={m.color}
          />

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
            <PrimaryButton onClick={() => setCurrentStep(2)} disabled={!problemData.description.trim()} mode={mode}>
              次へ：壁を特定する <ArrowRight size={15} />
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* ─── STEP 2: 壁を特定する ─── */}
      {!showHistory && currentStep === 2 && (
        <div style={{ animation: 'slideUp 0.3s ease' }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Target size={18} color={m.color} />
              {m.step2Title}
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280', paddingLeft: 26, lineHeight: 1.6 }}>
              {m.step2Desc}
            </p>
          </div>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '13px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 5 }}>あなたの課題</div>
            <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              「{problemData.description.slice(0, 70)}{problemData.description.length > 70 ? '…' : ''}」
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: 12, alignItems: 'center', marginBottom: 22 }}>
            <div>
              <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600, marginBottom: 7 }}>{m.improveLabel}</div>
              <StyledSelect value={problemData.improvingFactor} onChange={e => setProblemData({ ...problemData, improvingFactor: e.target.value })} options={params} placeholder="選択してください" accentColor={m.color} />
            </div>
            <div style={{ textAlign: 'center', color: '#d1d5db', fontSize: 14, marginTop: 24 }}>vs</div>
            <div>
              <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, marginBottom: 7 }}>{m.worsenLabel}</div>
              <StyledSelect value={problemData.worseningFactor} onChange={e => setProblemData({ ...problemData, worseningFactor: e.target.value })} options={params} placeholder="選択してください" accentColor={m.color} />
            </div>
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '11px 15px', marginBottom: 16, fontSize: 13, color: '#dc2626' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setCurrentStep(1)}
              style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '10px 20px', color: '#6b7280', fontSize: 13, cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#9ca3af'; e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
            >
              ← 戻る
            </button>
            <PrimaryButton onClick={handleAnalyze} loading={loading} mode={mode}>
              <Lightbulb size={15} />
              突破口を見つける
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* ─── STEP 3: 突破口を受け取る ─── */}
      {!showHistory && currentStep === 3 && (
        <div style={{ animation: 'slideUp 0.35s ease' }}>
          <div style={{ marginBottom: 20 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lightbulb size={18} color="#d97706" />
              {m.step3Title}
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: '#6b7280', paddingLeft: 26 }}>
              {m.step3Desc}
            </p>
          </div>

          {/* 課題サマリー */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ background: m.tagBg, color: m.tagColor, fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
                {m.historyTag}
              </span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>分析した課題</span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
              「{problemData.description.slice(0, 80)}{problemData.description.length > 80 ? '…' : ''}」
            </p>
            {(problemData.improvingFactor || problemData.worseningFactor) && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {problemData.improvingFactor && (
                  <span style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', fontSize: 11, borderRadius: 20, padding: '3px 10px' }}>
                    ↑ {params.find(p => String(p.id) === problemData.improvingFactor)?.name}
                  </span>
                )}
                {problemData.worseningFactor && (
                  <span style={{ background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontSize: 11, borderRadius: 20, padding: '3px 10px' }}>
                    ↓ {params.find(p => String(p.id) === problemData.worseningFactor)?.name}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* 0件 */}
          {analyzedResults && analyzedResults.length === 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '20px', textAlign: 'center', marginBottom: 20, color: '#92400e' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>🔍</div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>マッチする法則が見つかりませんでした</p>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#b45309' }}>課題の説明をより具体的に書き直してみてください。</p>
            </div>
          )}

          {/* 突破口カード */}
          {analyzedResults && analyzedResults.map((result, i) => (
            <BreakthroughCard key={i} result={result} rank={i} mode={mode} />
          ))}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
            <button
              onClick={handleRestart}
              style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 22px', color: '#6b7280', fontSize: 13, cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = m.color; e.currentTarget.style.color = m.color; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
            >
              <RotateCcw size={14} />
              別の課題を分析する
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
